/*
  FD brand: the "does not apply" resolver.

  A corner sofa has a Size and a Chaise side, but the Symmetrical size has no
  handing. Left to itself Dawn leaves the shopper on a dead end: Symmetrical
  plus Left-hand is not a variant, so the price freezes and the basket button
  reads "unavailable" until they work out that a third chaise value exists and
  click it. The mock instead greys the whole chaise group as "does not apply"
  and resolves the sofa for them.

  Nothing about Symmetrical, chaise sides or this product is written into this
  file. The rule is general: after Dawn re-renders the pickers, an option whose
  values are all unavailable EXCEPT ONE is an option the shopper has no decision
  left in. That one value is selected for them and the group is greyed and
  frozen. It unfreezes by itself when a size that does offer a choice is picked,
  because the re-render restores the availability that drove it.

  Availability comes from the server, not from a variant table held here: Dawn
  re-fetches this section on every option change and Shopify recomputes each
  value's availability against the rest of the selection. That also means this
  keeps working on a product with more than 250 variants, where Liquid would
  refuse to hand a full variant list to the page.
*/
if (!customElements.get('fd-option-resolver')) {
  customElements.define(
    'fd-option-resolver',
    class FdOptionResolver extends HTMLElement {
      connectedCallback() {
        // Dawn replaces the contents of <variant-selects>, not the element
        // itself, so this wrapper survives every re-render and its observer
        // with it.
        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(this, { childList: true, subtree: true });
        this.schedule();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      schedule() {
        if (this.queued) return;
        this.queued = true;
        // A timeout, not requestAnimationFrame: rAF is frozen while the tab is
        // in the background, which would leave a shopper returning to the page
        // looking at an unresolved dead end until something repainted.
        window.setTimeout(() => {
          this.queued = false;
          this.resolve();
        }, 0);
      }

      resolve() {
        // Which options this has answered on the shopper's behalf. Dawn rebuilds
        // the fieldsets on every render, so the memory is kept here, by option
        // name, rather than on the markup.
        this.forced = this.forced || new Set();

        // Dawn's view transition puts the incoming pickers in the DOM before it
        // takes the outgoing ones out, so for a few frames every option has TWO
        // fieldsets. Acting then means acting on the copy that is about to be
        // thrown away -- which looked exactly like the resolver not working.
        // Wait for the swap to finish instead.
        const fieldsets = Array.from(this.querySelectorAll('fieldset.fd-pick'));
        const seen = new Set();
        const duplicated = fieldsets.some((fieldset) => {
          const input = fieldset.querySelector('input[type="radio"]');
          const key = input?.dataset.optionName;
          if (!key) return false;
          if (seen.has(key)) return true;
          seen.add(key);
          return false;
        });
        if (duplicated) {
          window.setTimeout(() => this.resolve(), 60);
          return;
        }

        fieldsets.forEach((fieldset) => {
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          if (inputs.length < 2) return;

          const name = inputs[0].dataset.optionName;
          const usable = inputs.filter((input) => !input.classList.contains('disabled'));
          const checked = inputs.find((input) => input.checked);
          const note = fieldset.querySelector('[data-fd-option-na]');
          if (!usable.length) return;

          if (usable.length === 1) {
            fieldset.classList.add('fd-pick--na');
            if (note) note.hidden = false;
            // Remembered whether or not a click was needed: the shopper may
            // already have been sitting on the only possible value, and it is
            // still this resolver's answer to undo when the choice returns.
            this.forced.add(name);

            if (!usable[0].checked) {
              // Selecting it re-renders the section; the pass that follows is
              // the one that freezes the group, so nothing is disabled in
              // flight.
              inputs.forEach((input) => {
                input.disabled = false;
              });
              usable[0].click();
              return;
            }

            inputs.forEach((input) => {
              input.disabled = input !== usable[0];
            });
            return;
          }

          fieldset.classList.remove('fd-pick--na');
          if (note) note.hidden = true;
          inputs.forEach((input) => {
            input.disabled = false;
          });

          // The option has a real choice again, but it is still sitting on the
          // value THIS put there, which the new selection does not offer. Undo
          // it rather than leaving a dead end in the other direction. Only a
          // value this resolver chose is ever moved: a shopper's own choice
          // that goes unavailable is left alone for them to change.
          if (this.forced.has(name)) {
            this.forced.delete(name);
            if (checked && checked.classList.contains('disabled')) usable[0].click();
          }
        });
      }
    }
  );
}
